import {
  INQUIRY_VALIDATION,
  type B2bInquiryPayload,
  type B2bRequirementKey,
  type CorporateEnquiryForm,
  type VendorInquiryPayload,
  type VendorRegistrationForm,
} from '../constants/inquiries'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function emptyToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

export function phoneDigits(value: string): string {
  return value.replace(/\D/g, '')
}

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim())
}

export function isValidPhone(value: string): boolean {
  const digits = phoneDigits(value)
  return digits.length === 10 || (digits.length >= 10 && digits.length <= 15)
}

export function normalizePhone(value: string): string {
  const digits = phoneDigits(value)
  if (digits.length === 10) return `+91${digits}`
  if (digits.startsWith('91') && digits.length === 12) return `+${digits}`
  if (value.trim().startsWith('+')) return `+${digits}`
  return digits
}

export function vendorInquiryPayload(
  form: VendorRegistrationForm,
): VendorInquiryPayload {
  return {
    username: form.username.trim(),
    email: form.email.trim(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    storeName: form.storeName.trim(),
    address1: form.address1.trim(),
    address2: emptyToNull(form.address2),
    country: form.country.trim(),
    city: emptyToNull(form.city),
    state: form.state.trim(),
    postcode: form.postcode.trim(),
    storePhone: normalizePhone(form.storePhone),
  }
}

export function b2bInquiryPayload(form: CorporateEnquiryForm): B2bInquiryPayload {
  return {
    name: form.name.trim(),
    company: form.company.trim(),
    email: form.email.trim(),
    phone: normalizePhone(form.phone),
    requirements: form.requirements,
    description: form.description.trim(),
    deliveryCity: form.deliveryCity.trim(),
    preferredDate: emptyToNull(form.preferredDate),
    quantity: emptyToNull(form.quantity),
    budget: emptyToNull(form.budget),
    gstInvoice: form.gstInvoice,
  }
}

export function validateVendorRegistration(
  form: VendorRegistrationForm,
): string | null {
  const required = [
    form.username,
    form.email,
    form.firstName,
    form.lastName,
    form.storeName,
    form.address1,
    form.country,
    form.state,
    form.postcode,
    form.storePhone,
  ]
  if (required.some((value) => !value.trim())) return INQUIRY_VALIDATION.required
  if (!isValidEmail(form.email)) return INQUIRY_VALIDATION.email
  if (!isValidPhone(form.storePhone)) return INQUIRY_VALIDATION.phone
  return null
}

export function validateCorporateEnquiry(
  form: CorporateEnquiryForm,
): string | null {
  const required = [
    form.name,
    form.company,
    form.email,
    form.phone,
    form.description,
    form.deliveryCity,
  ]
  if (required.some((value) => !value.trim())) return INQUIRY_VALIDATION.required
  if (!isValidEmail(form.email)) return INQUIRY_VALIDATION.email
  if (!isValidPhone(form.phone)) return INQUIRY_VALIDATION.phone
  if (form.requirements.length === 0) return INQUIRY_VALIDATION.requirements
  if (form.description.trim().length < 8) return INQUIRY_VALIDATION.description
  return null
}

export function toggleRequirement(
  current: B2bRequirementKey[],
  id: B2bRequirementKey,
): B2bRequirementKey[] {
  return current.includes(id)
    ? current.filter((item) => item !== id)
    : [...current, id]
}
